'use client'

// The one mounted profile editor. Any surface calls `openProfileEditor()`;
// this host supplies the viewer's wallet, account and current profile, and
// after a save pushes the result into the caches every surface reads — the
// identity index (names and faces on rows) and the profile read — so nothing
// re-fetches a profile the browser still has cached.

import React from 'react'
import { useSWRConfig } from 'swr'
import { useTwetchUi } from '../lib/twetch-ui'
import useViewerProfile, { viewerProfileKey, type ViewerProfile } from '../lib/use-viewer-profile'
import { updateIdentity } from '../lib/useTwetchIdentities'
import TwetchProfileEditor, { type SavedTwetchProfile } from './twetch-profile-editor'

// Several surfaces mount a host (the site header, /wallet, the terminal, a
// collection's Social tab) and more than one can be on screen. Only the first
// mounted draws the editor, so one `openProfileEditor()` opens one dialog.
const hosts: symbol[] = []
const hostListeners = new Set<() => void>()
const notifyHosts = () => hostListeners.forEach((listener) => listener())
function subscribeHosts(listener: () => void): () => void {
	hostListeners.add(listener)
	return () => hostListeners.delete(listener)
}

function useIsPrimaryHost(): boolean {
	const [id] = React.useState(() => Symbol('twetch-profile-host'))
	React.useEffect(() => {
		hosts.push(id)
		notifyHosts()
		return () => {
			hosts.splice(hosts.indexOf(id), 1)
			notifyHosts()
		}
	}, [id])
	return React.useSyncExternalStore(
		subscribeHosts,
		() => hosts[0] === id,
		() => false
	)
}

export default function TwetchProfileHost() {
	const primary = useIsPrimaryHost()
	const open = useTwetchUi((s) => s.profileEditorOpen)
	const close = useTwetchUi((s) => s.closeProfileEditor)
	const viewer = useViewerProfile()
	const { mutate } = useSWRConfig()

	if (!primary || !open || !viewer.address) {
		return null
	}
	const address = viewer.address
	const onSaved = (saved: SavedTwetchProfile) => {
		updateIdentity(address, { id: saved.id, name: saved.name, icon: saved.icon })
		void mutate(
			viewerProfileKey(saved.id),
			(current: ViewerProfile | undefined) => ({
				...current,
				id: saved.id,
				name: saved.name,
				description: saved.description,
				icon: saved.icon ?? current?.icon
			}),
			{ revalidate: false }
		)
	}
	return (
		<TwetchProfileEditor
			open
			onOpenChange={(next) => {
				if (!next) close()
			}}
			address={address}
			hasAccount={viewer.userId !== null}
			profile={viewer.profile ?? (viewer.name ? { name: viewer.name, icon: viewer.icon } : null)}
			onSaved={onSaved}
		/>
	)
}
